import { StrictMode, Component } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.jsx'
import { installShim } from './shim/index.js'

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    this.setState({ error, errorInfo });
    // Report to backend error logger (fire-and-forget, respects opt-out)
    try {
      if (localStorage.getItem('error_reporting') !== 'false') {
        fetch(`http://${window.location.hostname}:3001/api/error-report`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            category: 'frontend',
            code: error?.name || 'REACT_ERROR',
            message: error?.message || 'Unknown frontend error',
            context: {
              componentStack: (errorInfo?.componentStack || '').substring(0, 300),
              url: window.location.href,
              userAgent: navigator.userAgent,
            }
          })
        }).catch(() => {});
      }
    } catch {}
  }

  generateReport() {
    const { error, errorInfo } = this.state;
    const report = {
      app: 'Qlipper AI',
      version: typeof __APP_VERSION_SHORT__ !== 'undefined' ? __APP_VERSION_SHORT__ : 'unknown',
      platform: navigator.platform,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      error: error?.toString() || 'Unknown error',
      stack: error?.stack || '',
      componentStack: errorInfo?.componentStack || '',
    };
    return JSON.stringify(report, null, 2);
  }

  render() {
    if (this.state.hasError) {
      const isInsertBeforeError = this.state.error?.message?.includes('insertBefore') ||
                                   this.state.error?.message?.includes('appendChild') ||
                                   this.state.error?.message?.includes('removeChild');
      return (
        <div style={{
          padding: '2rem',
          background: '#020617',
          color: '#e2e8f0',
          minHeight: '100vh',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ maxWidth: '480px', width: '100%', textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem', color: '#f59e0b' }}>
              Terjadi Kesalahan
            </h1>
            <p style={{ color: '#94a3b8', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              {isInsertBeforeError
                ? 'Terjadi error tampilan. Biasanya sementara — klik Muat Ulang untuk melanjutkan.'
                : 'Terjadi error yang tidak terduga. Data kamu aman.'}
            </p>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginBottom: '1.5rem' }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f59e0b',
                  color: '#000',
                  border: 'none',
                  borderRadius: '0.75rem',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                }}
              >
                Muat Ulang
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(this.generateReport());
                  this.setState({ copied: true });
                  setTimeout(() => this.setState({ copied: false }), 2000);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#1e293b',
                  color: '#94a3b8',
                  border: '1px solid #334155',
                  borderRadius: '0.75rem',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                }}
              >
                {this.state.copied ? '✓ Tersalin!' : 'Salin Laporan Error'}
              </button>
            </div>

            <details style={{ textAlign: 'left' }}>
              <summary style={{ color: '#475569', cursor: 'pointer', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                Detail Teknis
              </summary>
              <pre style={{
                background: '#1e293b',
                padding: '1rem',
                borderRadius: '0.5rem',
                overflow: 'auto',
                fontSize: '0.7rem',
                color: '#ef4444',
                maxHeight: '200px',
              }}>
                {this.state.error && this.state.error.toString()}
                {'\n'}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Install the client-side backend shim BEFORE the app mounts, so the very first
// /api/* call (and native media fetches via the service worker) are intercepted.
installShim().finally(() => {
  createRoot(document.getElementById('root')).render(
    <StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </StrictMode>,
  )
})
